import { useCallback, useState } from 'react'
import { useToast } from '@/hooks/use-toast'
import AuthCard from './auth-card'
import PasswordRequirements from './password-requirements'
import Label from '../label'
import ToggleButton from '../toggle-button'
import Button from '../button'
import Loader from '../loader'
import { AUTH_CONFIG } from '../../auth/config'

const SignUp = () => {
  const { toast } = useToast()
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showRetypePassword, setShowRetypePassword] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword)
  }
  const toggleRetypePasswordVisibility = () => {
    setShowRetypePassword(!showRetypePassword)
  }

  const handleEmailChange = useCallback(
    (e) => {
      setEmail(e.target.value)
    },
    [setEmail],
  )

  const handlePasswordChange = useCallback(
    (e) => {
      setPassword(e.target.value)
    },
    [setPassword],
  )

  const handleRetypePasswordChange = useCallback(
    (e) => {
      if (password !== e.target.value) {
        setError('Passwords do not match!')
      } else {
        setError('')
      }
    },
    [password],
  )

  const handleRegister = useCallback(
    async (e) => {
      e.preventDefault()
      setLoading(true)

      try {
        const response = await fetch(`${AUTH_CONFIG.authServerUrl}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        })

        if (!response.ok) {
          const data = await response.json().catch(() => ({}))
          throw new Error(data.message || `Registration failed (${response.status})`)
        }

        toast({
          title: 'Registration successful!',
          description: `Please check your email (${email}) for confirmation.`,
          variant: 'success',
        })

        setTimeout(() => {
          window.location.href = `${AUTH_CONFIG.authServerUrl}/login`
        }, 3000)
      } catch (err) {
        console.log('Error:', err)
        toast({
          title: 'Error!',
          description: `${err.message}`,
          variant: 'destructive',
        })
      } finally {
        setLoading(false)
      }
    },
    [email, password, toast],
  )

  return (
    <AuthCard title="Register">
      <div className="relative max-w-md mx-auto p-4">
        {loading && <Loader />}
        <form onSubmit={handleRegister}>
          <div className="mb-4">
            <Label text="Email" htmlFor="email" />
            <input
              type="email"
              id="email"
              name="email"
              className="w-full px-3 py-2 bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Email"
              required
              onChange={handleEmailChange}
            />
          </div>
          <PasswordRequirements />
          <div className="mb-4 relative">
            <Label text="New Password" htmlFor="new-password" />
            <input
              type={showNewPassword ? 'text' : 'password'}
              id="new-password"
              name="new-password"
              className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="New Password"
              required
              onChange={handlePasswordChange}
            />
            <ToggleButton
              toggleFunc={toggleNewPasswordVisibility}
              toggled={showNewPassword}
              showImagePath="/eye.svg"
              hideImagePath="/eye-closed.svg"
            />
          </div>

          <div className="mb-8 relative">
            <Label text="Retype Password" htmlFor="retype-password" />
            <input
              type={showRetypePassword ? 'text' : 'password'}
              id="retype-password"
              name="retype-password"
              className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Retype Password"
              required
              onChange={handleRetypePasswordChange}
            />
            <ToggleButton
              toggleFunc={toggleRetypePasswordVisibility}
              toggled={showRetypePassword}
              showImagePath="/eye.svg"
              hideImagePath="/eye-closed.svg"
            />
          </div>

          {error && <p className="text-sm text-red-500 mb-4">{error}</p>}

          <div className="text-center">
            <Button type="submit" disabled={loading || error ? true : false}>
              Register
            </Button>
          </div>
        </form>
      </div>
    </AuthCard>
  )
}

export default SignUp
